<template>
  <div>
    <v-row no-gutters>
      <v-col cols="4">
        <v-autocomplete
          v-model="countryCode"
          @change="handleChange"
          :items="countries"
          :rules="[required]"
          :label="$t('forms.countryCode')"
          :loading="loading"
          item-text="text"
          item-value="value"
          class="country-code"
          single-line
          outlined
        >
          <template v-slot:selection="{ item }">
            {{ item.flag }} {{ item.value }}
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="8">
        <v-text-field
          v-model="phone"
          @input="handleInput"
          :rules="fieldRules"
          :label="fieldLabel"
          :loading="loading"
          class="phone-number"
          ref="phoneNumber"
          maxlength="15"
          outlined
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { dialCodeList } from '../../util/enums'
import { required } from '../../util/errorHandling'
import { curatePhoneNumber } from '../../util/helpers'

export default {
  name: 'HPhone',

  props: {
    rules: {
      type: Array,
      required: false,
    },

    defaultValue: {
      type: [Object, String],
      required: false,
    },

    label: {
      type: String,
      required: false,
      default: undefined,
    },

    country: {
      type: String,
      required: false,
      default: 'BR',
    },

    isAppointment: {
      type: Boolean,
      required: false,
      default: false,
    },

    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  computed: {
    countries() {
      const list = dialCodeList.map((country) => {
        return {
          text: `${country.flag} ${country.dial_code} ${country.name}`,
          value: country.dial_code,
          flag: country.flag,
        }
      })

      return list
    },

    fieldLabel() {
      if (!this.label) return this.$t('forms.phone')
      return this.label
    },

    fieldRules() {
      if (!this.rules) return []
      return this.rules ?? []
    },

    countryCode: {
      get() {
        return this.setInitialCountryCode()
      },
      set(value) {
        return value
      },
    },

    phone: {
      get() {
        return this.setInitialPhoneNumber()
      },
      set(value) {
        return value
      }
    }
  },

  methods: {
    ...mapMutations(['setAppointmentFields']),

    handleChange(countryCode) {
      const curatedPhone = curatePhoneNumber(this.phone)
      const phoneField = this.$refs.phoneNumber

      this.phone = ''
      if (phoneField) phoneField.reset()

      this.isAppointment
        ? this.setAppointmentFields({ visitor_phone: '' })
        : this.$emit('input', '')

      this.$nextTick(() => {
        this.currentCountryCode = countryCode

        this.isAppointment
          ? this.setAppointmentFields({ visitor_phone: `${countryCode} ${curatedPhone && curatedPhone['phone_number']}` })
          : this.$emit('input', `${countryCode} ${curatedPhone && curatedPhone['phone_number']}`)
      })
    },

    handleInput(phone) {
      const countryCode = this.currentCountryCode ?? this.countryCode
      const curatedPhone = curatePhoneNumber(phone)

      this.isAppointment
        ? this.setAppointmentFields({ visitor_phone: `${countryCode} ${curatedPhone && curatedPhone['phone_number']}` })
        : this.$emit('input', `${countryCode} ${curatedPhone && curatedPhone['phone_number']}`)
    },

    setInitialPhoneNumber() {
      if (!this.defaultValue) return ''

      const curatedPhone = curatePhoneNumber(this.defaultValue)
      this.$emit('input', `${curatedPhone['country_code'] ? curatedPhone['country_code'] : '+55'} ${curatedPhone['phone_number']}`)

      return curatedPhone['phone_number']
    },

    setInitialCountryCode() {
      if (!this.defaultValue) return '+55'

      const curatedPhone = curatePhoneNumber(this.defaultValue)
      this.currentCountryCode = curatedPhone['country_code']

      return curatedPhone['country_code']
    },

    findCountryCode(countryAcronym) {
      const country = dialCodeList.find((country) => country.code.includes(countryAcronym))
      return country?.dial_code
    }
  },

  data() {
    return {
      required,
      currentCountryCode: '',
      currentPhone: ''
    }
  }
}
</script>

<style scoped>
.country-code {
  position: relative;
  left: 1px;
  border-right: none !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.phone-number {
  border-left: none !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
</style>
